'use client';

import { useKeyPress, useMemoizedFn } from 'ahooks';
import { useTranslations } from 'next-intl';
import { useEffect, useMemo, useRef } from 'react';
// import { AgentLineSkillScript } from '@/app/components/icons/AgentLineSkillScript';
import { BaseLineClose } from '@/app/components/icons/BaseLineClose';
import { toast } from '@/app/components/ui/use-toast';
import { SCENE_INPUT_CONFIG } from '@/app/constants';
import { useStore } from '@/app/providers/store';
import type {
  AgentMediaItem,
  AgentReferenceContent,
  AgentSceneConfig,
  AgentSceneType,
  AgentSettings,
  AgentVoiceOption,
  TextPromptWithInputItem,
} from '@/app/types';
import { cn, reportEvent } from '@/app/utils';
import { useInputValidation } from '../../_hooks/use-input-validation';
import type { ModelOption, RichTextareaRef } from '../../_types';
import { ReferencePreview } from '../Chat/components/display/ReferencePreview';
import { UploadedFilesPreview } from '../Chat/components/display/UploadedFilesPreview';
import { ChatSettings } from './ChatSettings';
import { ConnectToInternet } from './ConnectToInternet';
import { FileUploadButton } from './FileUploadButton';
import { GlowingCard } from './GlowingCard';
import { ModelSelector } from './ModelSelector';
import { RichTextarea } from './RichTextarea';
import { SendButton } from './SendButton';

// import { useSceneOptions } from '../../_hooks/use-scene-options';
// import { ProductLineTemplates2 } from '@/app/components/icons/ProductLineTemplates2';
// import { CommonSelect } from '@/app/components/shared/CommonSelect';
// import { CustomTooltip } from '@/app/components/shared/CustomTooltip';

/**
 * ChatInput Props
 */
interface ChatInputProps {
  /** 聊天 ID */
  chatId?: string;
  /** 发送回调 */
  onSend: (params: {
    chatId?: string;
    sceneId: AgentSceneType;
    selectedSettings: AgentSettings;
    inputValue: TextPromptWithInputItem;
    uploadList: AgentMediaItem[];
    reference?: AgentReferenceContent;
    voiceId: string;
  }) => void;
  /** 语音  */
  selectedVoice: AgentVoiceOption;
  /** 当前设置 */
  settings: AgentSettings;
  /** 设置配置 */
  settingsConfig: AgentSceneConfig['settingsConfig'];
  /** 更新设置回调 */
  onUpdateSetting: <K extends keyof AgentSettings>(key: K, value: AgentSettings[K]) => void;
  /** 自定义类名 */
  className?: string;
  sceneId: AgentSceneType;
  onUpdateSceneId: (value: AgentSceneType) => void;
  // 输入相关状态
  /** 输入值 */
  inputValue: TextPromptWithInputItem;
  /** 引用内容 */
  reference?: AgentReferenceContent;
  /** 上传列表 */
  uploadList: AgentMediaItem[];
  /** 发送状态 */
  sending: boolean;
  // 回调函数
  /** 输入值变化回调 */
  onInputChange: (value: TextPromptWithInputItem) => void;
  /** 清除引用回调 */
  onClearReference?: () => void;
  /** 移除文件回调 */
  onRemoveFile: (id: string) => void;
  /** 更新语音回调 */
  onUpdateSelectedVoice: (selectedVoice: AgentVoiceOption) => void;
  size?: 'small' | 'large';
  /** 添加模型回调 */
  onAddModel: (model: ModelOption) => void;
  selectImages: () => void;
  selectVideo: () => void;
  canUpload: (type: 'image' | 'video') => boolean;
  disabled?: boolean;
  selectedSkills?: ChatInputSkillOption[];
  onRemoveSkill?: (skillId: string) => void;
  onClearSkills?: () => void;
}

export interface ChatInputSkillOption {
  icon?: React.ReactNode;
  id: string;
  label: string;
}

const SKILL_PROTOCOL_PREFIX = '<<SKILLS:';
const SKILL_PROTOCOL_SUFFIX = '>>';

const sanitizeSkillField = (value: string) => value.replace(/[|,<>]/g, ' ').trim();

const buildSkillProtocolText = (skills: ChatInputSkillOption[]) => {
  if (!skills.length) return '';
  const payload = skills.map((skill) => `${sanitizeSkillField(skill.id)}|${sanitizeSkillField(skill.label)}`).join(',');
  return `${SKILL_PROTOCOL_PREFIX}${payload}${SKILL_PROTOCOL_SUFFIX}\n`;
};

const prependSkillProtocolToInput = ({
  inputValue,
  selectedSkills,
}: {
  inputValue: TextPromptWithInputItem;
  selectedSkills: ChatInputSkillOption[];
}): TextPromptWithInputItem => {
  const protocolText = buildSkillProtocolText(selectedSkills);
  if (!protocolText) return inputValue;

  const firstItem = inputValue[0];
  if (firstItem?.type === 'text') {
    return [{ ...firstItem, text: `${protocolText}${firstItem.text}` }, ...inputValue.slice(1)];
  }

  return [{ type: 'text', text: protocolText }, ...inputValue];
};

/**
 * 输入区域组件
 */
function InputArea({
  inputValue,
  reference,
  uploadList,
  placeholder,
  size,
  onInputChange,
  onClearReference,
  onRemoveFile,
  handleSend,
  disabled,
  selectedSkills,
  onRemoveSkill,
}: {
  inputValue: TextPromptWithInputItem;
  reference?: AgentReferenceContent;
  uploadList: AgentMediaItem[];
  placeholder: string;
  size?: 'small' | 'large';
  onInputChange: (value: TextPromptWithInputItem) => void;
  onClearReference?: () => void;
  onRemoveFile: (id: string) => void;
  handleSend: () => void;
  disabled?: boolean;
  selectedSkills?: ChatInputSkillOption[];
  onRemoveSkill?: (skillId: string) => void;
}) {
  const openAgentViewResourceDialog = useStore((state) => state.openAgentViewResourceDialog);
  const textareaRef = useRef<RichTextareaRef>(null);

  useEffect(() => {
    textareaRef.current?.focus();
  }, [uploadList.length]);

  useKeyPress(
    'enter',
    (e) => {
      /** 如果不是模特描述编辑输入框，则发送消息 */
      if (!(e.target instanceof HTMLElement && e.target.classList.contains('agent-model-input'))) {
        e.preventDefault();
        e.stopPropagation();
        handleSend();
      }
    },
    {
      target: textareaRef.current?.getEditor()?.view.dom,
      exactMatch: true,
      useCapture: true,
    },
  );

  useKeyPress(
    ['shift.enter', 'ctrl.enter', 'alt.enter', 'meta.enter'],
    () => {
      textareaRef.current?.enter();
    },
    {
      target: textareaRef.current?.getEditor()?.view.dom,
    },
  );

  const handlePreview = (items: AgentMediaItem[], activeId?: string) => {
    const previewList = items
      .filter((item) => item.status === 'done')
      .map((item) => ({
        id: item.id,
        type: item.type,
        url: item.url || '',
        poster: item.cover_url || item.url,
      }));
    previewList.length && openAgentViewResourceDialog(previewList, activeId);
  };

  return (
    <div
      className="flex flex-1 flex-col pt-3"
      onMouseDownCapture={(e) => {
        if (disabled) {
          e.stopPropagation();
          e.preventDefault();
        }
      }}
    >
      {/* 引用预览 */}
      <div
        className={cn(
          'transition-[height,opacity] duration-300 ease-in-out',
          reference ? 'h-[58px] opacity-100' : 'h-0 opacity-0',
        )}
      >
        {reference && <ReferencePreview reference={reference} onRemove={onClearReference} className="mb-3" />}
      </div>

      {/* 上传文件预览 */}
      <div
        className={cn(
          'transition-[height,opacity] duration-300 ease-in-out',
          uploadList.length > 0 ? 'h-16 opacity-100' : 'h-0 opacity-0',
        )}
      >
        <UploadedFilesPreview
          uploadList={uploadList}
          onRemove={onRemoveFile}
          className="mb-2"
          onPreview={handlePreview}
          showAllOnHover={true}
          autoRevokeObjectURL={true}
        />
      </div>

      {/* 技能选择 */}
      {selectedSkills?.length ? (
        <div className="mb-2 flex flex-wrap items-center gap-2">
          {selectedSkills.map((skill) => (
            <div
              key={skill.id}
              className="group flex h-6 cursor-pointer items-center gap-1 rounded-8 border border-system-white12 bg-system-white04 px-2 py-0 text-xs hover:bg-system-white12"
            >
              {skill.icon}
              <span>{skill.label}</span>
              <BaseLineClose className="hidden size-3.5 group-hover:block" onClick={() => onRemoveSkill?.(skill.id)} />
            </div>
          ))}
        </div>
      ) : null}

      {/* 文本输入框 */}
      <div className={cn('relative mb-2 h-[144px]', size === 'small' ? 'h-[144px]' : 'h-[126px]')}>
        <RichTextarea
          placeholder={placeholder}
          textPrompt={inputValue}
          updateTextPrompt={onInputChange}
          placeholderClassName={size === 'small' ? 'text-sm' : 'text-base'}
          editorClassName={size === 'small' ? 'text-sm' : 'text-base'}
          ref={textareaRef}
          disabled={disabled}
        />
      </div>
    </div>
  );
}

/**
 * 操作栏组件
 */
function ActionBar({
  disabledUploadTooltip,
  onSelectImages,
  onSelectVideo,
  // sceneId,
  // onUpdateSceneId,
  settingsConfig,
  selectedSettings,
  onUpdateSetting,
  canSend,
  sending,
  onSend,
  selectedVoice,
  onUpdateSelectedVoice,
  showSettingValue,
  onAddModel,
  onSelectSearch,
}: {
  sceneId: AgentSceneType;
  disabledUploadTooltip: { image?: string; video?: string };
  onSelectImages: () => void;
  onSelectVideo: () => void;
  onUpdateSceneId: (value: AgentSceneType) => void;
  settingsConfig: AgentSceneConfig['settingsConfig'];
  selectedSettings: AgentSettings;
  onUpdateSetting: <K extends keyof AgentSettings>(key: K, value: AgentSettings[K]) => void;
  canSend: boolean;
  sending: boolean;
  onSend: () => void;
  selectedVoice: AgentVoiceOption;
  onUpdateSelectedVoice: (selectedVoice: AgentVoiceOption) => void;
  showSettingValue?: boolean;
  onAddModel: (model: ModelOption) => void;
  onSelectSearch: () => void;
}) {
  // const t = useTranslations();
  // const { options: sceneOptions } = useSceneOptions();

  return (
    <div className="mb-3 flex h-8 flex-shrink-0 items-center justify-between">
      <div className="flex h-full items-center gap-2">
        {/* 文件上传按钮 */}
        <FileUploadButton
          disabledUploadTooltip={disabledUploadTooltip}
          onSelectImages={onSelectImages}
          onSelectVideo={onSelectVideo}
        />
        {/* 设置 Popover */}
        <ChatSettings
          config={settingsConfig}
          selectedSettings={selectedSettings}
          onUpdateSetting={onUpdateSetting}
          showValue={showSettingValue}
          selectedVoice={selectedVoice}
          onUpdateSelectedVoice={onUpdateSelectedVoice}
        />
        {/* 场景选择按钮 */}
        {/* <CommonSelect
          selectGroupClassName="gap-2"
          customTooltipVariant="glass"
          enableAnimation={true}
          value={sceneId}
          options={sceneOptions}
          onValueChange={(value) => onUpdateSceneId(value as AgentSceneType)}
          className="group flex h-full w-fit items-center whitespace-nowrap rounded-full border-system-white08 border-solid bg-system-white04 p-0 text-system-white48 text-xs hover:bg-system-white24 hover:text-system-white"
          contentClassName="bg-[rgba(34,37,42,0.48)] backdrop-blur-[30px] min-w-[365px] border border-system-white08"
          itemClassName="text-xs h-[72px] focus:bg-system-white08 data-[state=checked]:bg-system-white16"
          innerItemClassName="gap-2 px-2 py-3 text-sm"
          hideArrow={true}
          side="top"
          align="center"
          renderValue={(value) => (
            <CustomTooltip
              content={showSettingValue ? t('agentProject.sceneSelect.tip') : t('agentProject.dialogSettingsTip.scene')}
              variant="glass"
            >
              <div
                className={cn(
                  'flex items-center p-2 text-system-white48 text-xs group-hover:text-system-white',
                  showSettingValue && 'pr-3',
                )}
              >
                <div className={cn(showSettingValue && 'mr-2 border-system-white08 border-r pr-1.5')}>
                  <ProductLineTemplates2 className="size-4 flex-shrink-0 text-system-white" />
                </div>
                {showSettingValue ? sceneOptions.find((option) => option.value === value)?.label : null}
              </div>
            </CustomTooltip>
          )}
        /> */}

        {/* 模型选择 */}
        <ModelSelector onSelectModel={onAddModel} />

        {/* 是否联网 */}
        <ConnectToInternet onSelectSearch={onSelectSearch} selectedSettings={selectedSettings} />
      </div>

      {/* 发送按钮 */}
      <SendButton disabled={!canSend} sending={sending} onClick={onSend} />
    </div>
  );
}

/**
 * 聊天输入组件
 * 负责协调输入区域和操作栏
 */
export function ChatInput({
  onSend,
  className,
  settings,
  settingsConfig,
  onUpdateSetting,
  inputValue,
  reference,
  uploadList,
  sending,
  chatId,
  onInputChange,
  onClearReference,
  onRemoveFile,
  sceneId,
  selectedVoice,
  onUpdateSelectedVoice,
  onUpdateSceneId,
  size = 'large',
  onAddModel,
  selectImages,
  selectVideo,
  canUpload,
  disabled,
  selectedSkills = [],
  onRemoveSkill,
  onClearSkills,
}: ChatInputProps) {
  const t = useTranslations();

  // 输入验证
  const { canSend, hasValidContent } = useInputValidation({
    inputValue,
    uploadList,
    sending,
    reference,
  });

  const { agentProjectDetail } = useStore((state) => ({
    agentProjectDetail: state.agentProjectDetail,
  }));

  const inputConfig = useMemo(() => SCENE_INPUT_CONFIG[sceneId], [sceneId]);

  /**
   * 发送消息处理
   * 验证已通过 canSend 完成，此处直接转发给父组件
   */
  const handleSend = useMemoizedFn(() => {
    if (canSend) {
      const inputValueWithSkills = prependSkillProtocolToInput({
        inputValue,
        selectedSkills,
      });

      onSend({
        chatId: chatId,
        sceneId: sceneId,
        selectedSettings: settings,
        inputValue: inputValueWithSkills,
        uploadList: uploadList,
        reference: reference,
        voiceId: selectedVoice.id || '',
      });
      onClearSkills?.();
      reportEvent('dialog_message_send_click', {
        project_id: agentProjectDetail?.id || '',
        session_id: chatId || '',
      });
    } else {
      if (hasValidContent) {
        toast({
          message: t('agentProject.chatDialog.thinkingToast'),
        });
      }
    }
  });

  const handleSelectSearch = useMemoizedFn(() => {
    if (disabled) {
      return;
    }
    onUpdateSetting('net_mode', settings.net_mode === 'search' ? undefined : 'search');
  });

  const disabledUploadTooltip = useMemo(() => {
    const allowedTypes = inputConfig?.allowInputTypes || [];
    /** 是否允许上传图片 */
    const isImageAllowed = allowedTypes.includes('image');
    /** 是否允许上传视频 */
    const isVideoAllowed = allowedTypes.includes('video');
    /** 图片数量是否达到上传限制 */
    const canUploadImage = canUpload('image');
    /** 视频数量是否达到上传限制 */
    const canUploadVideo = canUpload('video');
    return {
      image: isImageAllowed
        ? canUploadImage
          ? undefined
          : t('agentProject.chatDialog.maxImageUploadToast', {
              limit: inputConfig?.inputTypesLimit?.image || 0,
            })
        : t('agentProject.chatDialog.disableUploadToast', {
            type: t('agentProject.atType.image'),
          }),
      video: isVideoAllowed
        ? canUploadVideo
          ? undefined
          : t('agentProject.chatDialog.maxVideoUploadToast', {
              limit: inputConfig?.inputTypesLimit?.video || 0,
            })
        : t('agentProject.chatDialog.disableUploadToast', {
            type: t('agentProject.atType.video'),
          }),
    };
  }, [inputConfig?.allowInputTypes, inputConfig?.inputTypesLimit, sceneId, uploadList.length]);

  const content = (
    <>
      {/* 输入区域 */}
      <InputArea
        inputValue={inputValue}
        reference={reference}
        uploadList={uploadList}
        onInputChange={onInputChange}
        onClearReference={onClearReference}
        onRemoveFile={onRemoveFile}
        placeholder={t('agentProject.chatDialog.input.placeholder')}
        handleSend={handleSend}
        size={size}
        disabled={disabled}
        selectedSkills={selectedSkills}
        onRemoveSkill={onRemoveSkill}
      />

      {/* 操作栏 */}
      <ActionBar
        sceneId={sceneId}
        onUpdateSceneId={onUpdateSceneId}
        disabledUploadTooltip={disabledUploadTooltip}
        onSelectImages={selectImages}
        onSelectVideo={selectVideo}
        settingsConfig={settingsConfig}
        selectedSettings={settings}
        onUpdateSetting={onUpdateSetting}
        canSend={!!canSend}
        sending={sending}
        onSend={handleSend}
        selectedVoice={selectedVoice}
        onUpdateSelectedVoice={onUpdateSelectedVoice}
        showSettingValue={size === 'large'}
        onAddModel={onAddModel}
        onSelectSearch={handleSelectSearch}
      />
    </>
  );

  const innerClassName = cn(
    // 布局
    'flex w-full flex-col',
    // 盒模型
    'px-4',
  );

  if (size === 'large') {
    return (
      <GlowingCard className={className} innerClassName={innerClassName} glowSensitivity={30}>
        {content}
      </GlowingCard>
    );
  }

  return (
    <div
      className={cn(
        'border-system-white12 border-t',
        'bg-[linear-gradient(180deg,rgba(0,0,0,0.24)_0%,rgba(0,0,0,0.08)_100%)]',
        className,
        innerClassName,
      )}
    >
      {content}
    </div>
  );
}
